import React, { useEffect, useRef, useState, memo } from "react"
import { Loader } from "@kohler-dsm-library/kohler-core"
import styles from "@/components/Remedy/components/HeroBlockSection/components/HeroBlockVideo/HeroBlockVideo.module.scss"
import {
  handleVideoPlayedAnalytics,
  handleVideoEndedAnalytics,
} from "@/components/Remedy/components/analytics"

const HeroBlockVideo = ({
  shouldTriggerDive = () => {},
  isLoopingVideoLoaded = false,
  isAnimatedVideoLoaded = false,
  handleStateUpdate = () => {},
}) => {
  const loopVideoContainerRef = useRef(null)
  const animatedVideoContainerRef = useRef(null)
  const [loopVideoZIndex, setLoopVideoZIndex] = useState(1)
  const [animatedVideoZIndex, setAnimatedVideoZIndex] = useState(0)

  const handleDiveEffect = () => {
    if (!animatedVideoContainerRef.current) {
      return
    }

    const animatedVideo =
      animatedVideoContainerRef.current.querySelector("video")

    if (!animatedVideo) {
      return
    }

    const sourceVideo = animatedVideo.querySelector("source")

    animatedVideo.play()
    handleVideoPlayedAnalytics(
      "Ice Bath by Remedy Place",
      sourceVideo?.src,
      true,
      "heroanimatedvideo"
    )

    setLoopVideoZIndex(0)
    loopVideoContainerRef.current.style.visibility = "hidden"
    setAnimatedVideoZIndex(1)

    setTimeout(() => {
      setLoopVideoZIndex(1)
      loopVideoContainerRef.current.style.visibility = "visible"
      animatedVideoContainerRef.current.style.visibility = "hidden"
      setAnimatedVideoZIndex(0)
      handleVideoEndedAnalytics(
        "Ice Bath by Remedy Place",
        sourceVideo?.src,
        true,
        "heroanimatedvideo"
      )
    }, 2000)
  }

  const handleLoopVideo = () => {
    if (!loopVideoContainerRef.current) {
      return
    }
    const loopVideo = loopVideoContainerRef.current.querySelector("video")

    const sourceVideo = loopVideo.querySelector("source")

    const handleHeroVideoLoadedMetadata = () => {
      handleStateUpdate("loopvideo", true)

      if (!window.videoPlayTriggered) {
        handleVideoPlayedAnalytics(
          "Ice Bath by Remedy Place",
          sourceVideo?.src,
          true,
          "heroloopvideo"
        )
        window.videoPlayTriggered = true
        loopVideo.removeEventListener("canplay", handleHeroVideoLoadedMetadata)
      }
    }

    loopVideo.addEventListener("canplay", handleHeroVideoLoadedMetadata)

    return () => {
      loopVideo.removeEventListener("canplay", handleHeroVideoLoadedMetadata)
    }
  }

  const handleAnimatedVideo = () => {
    if (!animatedVideoContainerRef.current) {
      return
    }

    const animatedVideo =
      animatedVideoContainerRef.current.querySelector("video")

    const handleVideoLoadedMetadata = () => {
      animatedVideo.pause()
      handleStateUpdate("animation", true)
    }

    if (animatedVideo.readyState >= 3) {
      animatedVideo.pause()
      handleStateUpdate("animation", true)
    } else {
      animatedVideo.addEventListener(
        "loadedmetadata",
        handleVideoLoadedMetadata
      )
    }

    return () => {
      animatedVideo.removeEventListener(
        "loadedmetadata",
        handleVideoLoadedMetadata
      )
    }
  }

  useEffect(() => {
    if (shouldTriggerDive) {
      handleDiveEffect()
    }
  }, [shouldTriggerDive])

  useEffect(() => {
    const cleanupLoopVideo = handleLoopVideo()
    const cleanupAnimatedVideo = handleAnimatedVideo()

    return () => {
      cleanupLoopVideo()
      cleanupAnimatedVideo()
    }
  }, [])

  return (
    <div className={styles["pxp-hero-video-wrapper"]}>
      <div
        className={styles["pxp-hero-loader-frame"]}
        style={{
          display:
            isAnimatedVideoLoaded && isLoopingVideoLoaded ? "none" : "flex",
        }}
      >
        <Loader />
      </div>
      <div
        ref={loopVideoContainerRef}
        className={styles["pxp-hero-video-loop"]}
        style={{
          zIndex: loopVideoZIndex,
          opacity: isLoopingVideoLoaded ? 1 : 0,
        }}
        dangerouslySetInnerHTML={{
          __html: `
          <video preload="auto" autoplay muted loop playsinline style="z-index:1;opacity:1;width:100%;height:100vh" id="heroloopvideo">
            <source src="https://kohler.scene7.com/is/content/${
              process.env.NEXT_PUBLIC_AUTHORBASEURL?.includes("www.kohler.com")
                ? "kohlergbhprod"
                : "kohlergbhqa"
            }/PXP/HeaderLoop.mp4" />
          </video>
        `,
        }}
      />

      <div
        ref={animatedVideoContainerRef}
        className={styles["pxp-hero-video-animated"]}
        style={{
          zIndex: animatedVideoZIndex,
          opacity: isAnimatedVideoLoaded ? 1 : 0,
        }}
        dangerouslySetInnerHTML={{
          __html: `
          <video preload="auto" muted autoplay loop playsinline style="z-index:1;opacity:1;width:100%;height:100vh" id="heroanimatedvideo">
            <source src="https://kohler.scene7.com/is/content/${
              process.env.NEXT_PUBLIC_AUTHORBASEURL?.includes("www.kohler.com")
                ? "kohlergbhprod"
                : "kohlergbhqa"
            }/PXP/HeaderAnimated.mp4" />
          </video>
        `,
        }}
      />
    </div>
  )
}

const MemoizedHeroBlockVideo = memo(HeroBlockVideo)
export { MemoizedHeroBlockVideo }
